import { assign, omit, pick } from 'lodash';
import { createSelector } from 'reselect';
import { modules } from '@veritone/glc-redux';
import {
  BUILT_IN_SPEAKER_SEPARATION_ENGINES,
  BUILT_IN_TRANSCRIPTION_ENGINES,
  DEFAULT_TRANSCRIPTION_LANGUAGE,
  resolveLanguageEngines,
} from '../../../../utils/speechmatics/engines';
import { availableSpeechmaticsLanguages } from '../../../../utils/speechmatics';
import { selectApplicationConfigs } from '../app';
import { selectIsEngineAvailable } from './availability/selectors';

const {
  config: { getConfig },
} = modules;

export const selectConfig = (state: any) => getConfig<Window['config']>(state);

export const selectConfigEngines = createSelector(
  selectConfig,
  selectApplicationConfigs,
  (config, applicationConfigs) => {
    const transcriptionEngines = applicationConfigs.transcriptionEngines as
      | Record<string, string>
      | null
      | undefined;
    /**
     * The ORGANIZATION's override, and the only one there is.
     *
     * `transcriptionEngineId` used to be read from `window.config` too, via
     * the `pick` below, which made a data-center `config.js` edit the fast way
     * to repoint transcription without a release. It is deliberately no longer
     * in that list: the engine ids ship with the app now, and the application
     * config is the supported override. A `transcriptionEngineId` left in a
     * deployed `config.js` has no effect — set it on the organization instead.
     */
    const transcriptionEngineId = applicationConfigs.transcriptionEngineId as
      | string
      | undefined;
    const voiceMaskEngineId = applicationConfigs.voiceMaskEngineId as
      | string
      | undefined;
    const speakerSeparationEngineId =
      applicationConfigs.speakerSeparationEngineId as string | undefined;
    const configuredSpeakerSeparationEngines =
      applicationConfigs.speakerSeparationEngines as
        | Record<string, string>
        | null
        | undefined;

    let detectionEngineGPU = applicationConfigs.detectionEngineGPU as
      | string
      | undefined;

    // for some reason can't use the selectFeatureFlags selector
    const featureFlags = config?.featureFlags || {};
    const appConfigFeatureFlagsJSON = (applicationConfigs.featureFlags ||
      {}) as Record<string, boolean | number>;
    const useGPU =
      featureFlags.useGPU ||
      appConfigFeatureFlagsJSON.useGPU ||
      applicationConfigs.useGPU;

    /**
     * Language to engine id, for both engines that have one per language.
     *
     * The app's own table first, then the scalar config applied to the default
     * language, then the org's per-language map over the top — see
     * `resolveLanguageEngines`. These two maps are the answer to "what engine
     * runs this language", everywhere; the scalars below remain only because
     * the rest of the app still asks for the default language's engine by name.
     */
    const resolvedTranscriptionEngines = resolveLanguageEngines(
      BUILT_IN_TRANSCRIPTION_ENGINES,
      transcriptionEngineId,
      transcriptionEngines
    );
    const resolvedSpeakerSeparationEngines = resolveLanguageEngines(
      BUILT_IN_SPEAKER_SEPARATION_ENGINES,
      speakerSeparationEngineId,
      configuredSpeakerSeparationEngines
    );

    // might be cleaner way to code this
    const applicationConfigOverrides = {
      transcriptionEngines: resolvedTranscriptionEngines,
      speakerSeparationEngines: resolvedSpeakerSeparationEngines,
      // The scalars are now DERIVED from the maps rather than read beside
      // them, so the two cannot disagree: whatever resolves for the default
      // language is what `transcriptionEngineId` means.
      transcriptionEngineId:
        resolvedTranscriptionEngines[DEFAULT_TRANSCRIPTION_LANGUAGE],
      speakerSeparationEngineId:
        resolvedSpeakerSeparationEngines[DEFAULT_TRANSCRIPTION_LANGUAGE],
      ...(!!voiceMaskEngineId && { voiceMaskEngineId }),
      ...(!!detectionEngineGPU && { detectionEngineGPU }),
    };

    const transcriptionEngineOptions = omit(
      // read the value directly — pick() would return a { transcriptionEngineOptions: ... }
      // wrapper, leaving the options double-nested and this omit() a no-op (VE-24980)
      (applicationConfigs.transcriptionEngineOptions as
        | Record<string, unknown>
        | null
        | undefined) || {},
      // remove keys that should not be overwritten
      'app',
      'user',
      'organizationId'
    );

    const combined = assign(
      {
        outputWriterEngineId: '8eccf9cc-6b6d-4d7d-8cb3-7ebf4950c5f3',
        // legacy ingestion engines - keep for now to support old TDOs
        webstreamAdapterEngineId: '9e611ad7-2d3b-48f6-a51b-0a1ba40fe255',
        streamIngestorEngineId: '8bdb0e3b-ff28-4f6e-a3ba-887bd06e6440',
        streamIngestorDASHPlaybackSegmentCreatorId:
          '29694232-a4f5-4622-a422-b48f206ca52a',
        streamIngestorPlaybackSegmentCreatorId:
          '352556c7-de07-4d55-b33f-74b1cf237f25',
        // legacy redaction engine
        legacyRedactEngineId: '51263b9d-186b-4d09-a422-a8f12cc38f5c',
        // new engines
        /**
         * The per-language transcription and speaker-separation engines are
         * NOT here. They are resolved above into `transcriptionEngines` /
         * `speakerSeparationEngines` and the two scalars derived from them, in
         * `applicationConfigOverrides`, which is assigned over this object.
         * The tables themselves live in `utils/speechmatics/engines.ts`.
         *
         * Nothing is listed here as a fallback for them on purpose: a value
         * here would be unreachable (the overrides always set both) and would
         * read as a second opinion about which engine transcribes English.
         */
        voiceMaskEngineId: '9387281c-0f99-4453-a91f-1cc2449f2c99', // hard code default since no longer using data-center config (can override engine id using appConfig)
        detectionEngineGPU: '4949f413-f904-4771-bf25-c00e424de001', // hard code
        // Alternate - Image ingestion DAG engines (not used by default)
        si2EngineId: '8bdb0e3b-ff28-4f6e-a3ba-887bd06e6440',
        imageTranscodeEngineId: '8c0c2d4e-eedf-48ff-8b94-a26242e683cf',
        thumbnailGeneratorEngineId: 'fd891cb5-ff80-48ae-b5cd-ac3c7d190d55',
        /**
         * "speaker detection" category. Results are read back by category id
         * rather than engine id so that separation produced by either engine,
         * or by whichever one the config pointed at previously, still loads.
         *
         * The category is SHARED: it also covers Speaker Recognition V3F. That
         * makes membership of it a weaker claim than "this is speaker
         * separation output", and the app treats the two as the same thing -
         * loading, the running/failed indicators, and deleting all key off the
         * category alone. Safe only because Redact never runs recognition:
         * nothing in this repo creates a job for it, and the product does not
         * offer it. If that changes, these call sites need a discriminator
         * before the category can keep standing in for identity.
         */
        speakerDetectionCategoryId: 'a856c447-1030-4fb0-917f-08179f949c4e',
        // PDF converter engine for document ingestion - hard code default so it
        // works even when not present in data-center config (can override using appConfig)
        pdfConverterEngineId: 'eda09a05-2bbc-490a-96b6-3768875be88d',
        // Document PII detection engine - runs after the PDF converter when the
        // user opts in (FilePicker "Run PII" checkbox / re-run button). Hard code
        // default so it works without a data-center config entry (can override
        // using appConfig).
        documentPIIDetectionEngineId: '7a56a57f-366e-4b96-a2c7-8f1caa64f663',
        // Redact Document OCR engine - runs between the PDF converter
        // and PII detection, writing an invisible text layer onto scanned pages
        // and republishing the PDF as the TDO's primary `media` asset. Gated on
        // its own `runOCR` option, independent of PII detection (see
        // buildDocumentRedactionTasksAndRoutes): a document can be made
        // searchable without being scanned for PII, and vice versa. Setting
        // this to '' in the data-center config removes the stage entirely,
        // which is the rollback lever.
        documentOcrEngineId: '286e9f26-c865-4251-9e82-55aa62fb86c9',
      },
      pick(config, [
        'apiRoot',
        'defaultClusterId',

        'glcIngestionEngineId',
        'fastChunkerEngineId',

        'redactEngineId',

        'downloadEngineCategoryId',
        'downloadEngineId',

        'opticalTrackingEngineCatagoryId',
        'opticalTrackingEngineCategoryId',
        'opticalTrackingEngineId',

        'detectionCategory',
        'detectionEngineId',
        'detectionEngineGPU',

        'transcriptionCategoryId',
        'speakerDetectionCategoryId',
        'pdfConverterEngineId',
        'documentPIIDetectionEngineId',
        'documentOcrEngineId',
        'poiDetectionEngineId',

        // 'voiceMaskEngineId',
      ]),
      applicationConfigOverrides,
      { transcriptionEngineOptions }
    );

    // now choose which detectionEngine to use as the default

    const detectionEngineCPU = combined.detectionEngineId;
    detectionEngineGPU = combined.detectionEngineGPU;
    const detectionEngineIds = [detectionEngineCPU];
    if (combined.detectionEngineGPU) {
      detectionEngineIds.push(combined.detectionEngineGPU);
    }
    return {
      ...combined,
      detectionEngineCPU,
      detectionEngineId:
        useGPU && detectionEngineGPU ? detectionEngineGPU : detectionEngineCPU, // pick default detectionEngineId to run
      detectionEngineIds,
    };
  }
);

/**
 * The languages the transcription dropdown offers — one definition, for the
 * five surfaces that render that Select.
 *
 * Each of them used to carry its own
 * `availableSpeechmaticsLanguages.filter(({ code }) => transcriptionEngines?.[code])`,
 * which is how they came to disagree: the rule is now "we know an engine for
 * it AND this environment has that engine", and a rule with two halves is not
 * one to copy six times.
 *
 * Ordered by the catalogue rather than by the map, so the list reads
 * alphabetically by language name however the engines were configured.
 */
export const selectTranscriptionLanguageOptions = createSelector(
  selectConfigEngines,
  selectIsEngineAvailable,
  ({ transcriptionEngines }, isAvailable) =>
    availableSpeechmaticsLanguages.filter(({ code }) => {
      // Both halves, and `!!engineId` first: `isAvailable` answers "is there
      // anything stopping this engine running", so it says yes to `undefined`
      // — correct where there is no id to check, wrong here, where no id means
      // we do not know how to transcribe that language at all. Without this
      // the dropdown offered all 56 catalogue languages.
      const engineId = transcriptionEngines?.[code];
      return !!engineId && isAvailable(engineId);
    })
);

/**
 * Whether separation can be offered for a language — the ONLY answer to that
 * question, for the picker, the transcript tab and the run paths alike.
 *
 * BOTH engines, not just the separation one. Separation is downstream of
 * transcription in every surface that offers it: upload pairs the two jobs
 * (`ingestion/services.ts` drops separation unless `runTranscription`), and the
 * transcript tab separates a transcript that already exists. So a language we
 * cannot transcribe here is one we cannot separate here either, whatever the
 * separation engine's own state says — the converse does not hold, and a
 * language keeps its dropdown entry when only separation is missing.
 *
 * Each half is "we know an engine for it AND this environment has it", the
 * same two halves as the options list above. `!!engineId` first for the reason
 * given there: `isAvailable` says yes to `undefined`.
 */
export const selectSpeakerSeparationLanguageSupported = createSelector(
  selectConfigEngines,
  selectIsEngineAvailable,
  ({ speakerSeparationEngines, transcriptionEngines }, isAvailable) =>
    (languageCode: string | undefined): boolean => {
      if (!languageCode) {
        return false;
      }
      const separationEngineId = speakerSeparationEngines?.[languageCode];
      const transcriptionEngineId = transcriptionEngines?.[languageCode];
      return (
        !!separationEngineId &&
        isAvailable(separationEngineId) &&
        !!transcriptionEngineId &&
        isAvailable(transcriptionEngineId)
      );
    }
);
